import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, inputFormHeightOption, MaximizeIcon, RestoreIcon, SmallTitleSpan, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useKSK2021_R01Store } from "./Store_KSK2021_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultKSK2021_R01DetailDto, KSK2021_R01DetailDto, KSK2021_R01InspectDto } from "./Dto_KSK2021_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { getAllWorkPlaces } from "src/apis/workPlaceApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { getAllWarehouses } from "src/apis/warehouseApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";

const COLUMN_STATE_KEY = 'KSK2021_R01-detail-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['warehouseNm', 'childItemcd', 'itemNm', 'itemSpec', 'lotQty', 'bomUsingQty'],
	insertedDisabledCells: [''],
	commonDisabledCells: [''],
	
	pointerCells: ['transportQty'],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	alignLeftCells: [''],
};

interface Props {
	gridRef: React.RefObject<AgGridReact<KSK2021_R01DetailDto> | null>;
	defaultKSK2021_R01DetailRow: KSK2021_R01DetailDto;
	isMaximized?: boolean;
	onToggleMaximize?: () => void;
}

export const DetailTable_KSK2021_R01 = (props: Props) => {

	const { gridRef, defaultKSK2021_R01DetailRow, isMaximized = false, onToggleMaximize } = props;

	const { 
			KSK2021_R01DetailList, setKSK2021_R01DetailList, 
			editKSK2021_R01,
			selectedKSK2021_R01DetailList, setSelectedKSK2021_R01DetailList, removeSelectedKSK2021_R01DetailList, addSelectedKSK2021_R01DetailList, initSelectedKSK2021_R01DetailList,
			deletedKSK2021_R01DetailList, setDeletedKSK2021_R01DetailList, addDeletedKSK2021_R01DetailList, removeDeletedKSK2021_R01DetailList,
			insertKSK2021_R01DetailList, updateKSK2021_R01DetailList, removeKSK2021_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalKSK2021_R01DetailList, setOriginalKSK2021_R01DetailList,
		} = useKSK2021_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { itemStatusList } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: KSK2021_R01DetailList,
		defaultGridData: defaultKSK2021_R01DetailRow,
		selectedRow: selectedKSK2021_R01DetailList,
		originalGridData: originalKSK2021_R01DetailList,
		updateRow: (index: number, data: any) => updateKSK2021_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		warehouseNm: '출고창고',
		itemCd: '자재코드',
		itemNm: '품명',
		itemSpec: '규격',
		lotQty: '재고량',
		coordinateUsage: '소요량',
		transportQty: '출고량',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<KSK2021_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedKSK2021_R01DetailList,
            deletedList: deletedKSK2021_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedKSK2021_R01DetailList, deletedKSK2021_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);
	const baseColumnDefs: ColDef<KSK2021_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
		),
		{
            headerName: '출고창고', colId: 'warehouseNm', field: 'warehouseNm', width: 90,
			editable: false,
		},
        {
            headerName: '자재코드', colId: 'childItemcd', field: 'childItemcd', width: 150,
			editable: false,
		},
		{
            headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 350,
			editable: false,
		},
		{
            headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
			editable: false,
		},
		{
            headerName: '재고량', colId: 'lotQty', field: 'lotQty', width: 90,
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// {
		// 	headerName: '개당소요량', colId: 'coordinateUsage', field: 'coordinateUsage', width: 90,
		// 	editable: false,
		// },
		// {
		// 	headerName: '손실률', colId: 'lossPercent', field: 'lossPercent', width: 90,
		// 	editable: false,
		// },
		// {
		// 	headerName: '고정손실량', colId: 'fixLossqty', field: 'fixLossqty', width: 90,
		// 	editable: false,
		// },
		{
            headerName: '소요량', colId: 'bomUsingQty', field: 'bomUsingQty', width: 90,
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// {
        //		headerName: '출고완료량', colId: 'transportedQty', field: 'transportedQty', width: 65,
		//		editable: false,
		// },
		{
            headerName: '출고량', colId: 'transportQty', field: 'transportQty', width: 90,
			editable: true,
			pasteable: true,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// ...IndexParameterCell(),
	], [deletedKSK2021_R01DetailList, KSK2021_R01DetailList]);

	const columnDefs: ColDef<KSK2021_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<KSK2021_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: KSK2021_R01DetailList,
		originalDataList: originalKSK2021_R01DetailList,
		updateRow: updateKSK2021_R01DetailList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			if(field === 'transportQty') {
				if(updatedData.transportQty === 0) {
					updatedData.isModified = false;
				}
			}
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: KSK2021_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedKSK2021_R01DetailList,
		deletedList: deletedKSK2021_R01DetailList,
		addSelected: addSelectedKSK2021_R01DetailList,
		removeSelected: removeSelectedKSK2021_R01DetailList,
		clickable, setClickable
	});

	const commonInputColumns = ['transportQty', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['transportQty', ];


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: KSK2021_R01DetailList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<KSK2021_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(KSK2021_R01DetailList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2021_R01DetailDto>[] = useMemo(() => [
	], [editKSK2021_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK2021_R01DetailList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<KSK2021_R01DetailDto>) => {
		postSortHandler(params, KSK2021_R01DetailList, originalKSK2021_R01DetailList, setKSK2021_R01DetailList);
	}, [KSK2021_R01DetailList, originalKSK2021_R01DetailList, ]);

	const pinnedBottomRowData = useMemo(() => {
		return [{
			warehouseNm: KSK2021_R01DetailList.length.toLocaleString() + "건",
			// lotQty: KSK2021_R01DetailList.reduce((sum, item) => sum + (deletedKSK2021_R01DetailList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0),
		}];	  
	}, [KSK2021_R01DetailList, deletedKSK2021_R01DetailList,]);

	return (
		<>
			<div className="flex flex-1 pb-[24px] h-full min-h-full">
				<GridContextWrapper<KSK2021_R01DetailDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={undefined}
					// setPaddedGridData={setKSK2021_R01DetailList}
					initSelected={initSelectedKSK2021_R01DetailList}
					defaultRowData={defaultKSK2021_R01DetailRow}
					baseColumnDefs={columnDefs}	
					setColumnState={setDetailColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
			<SmallTitleSpan>
				자재출고
				<div className="flex items-center gap-[4px] ml-auto">
					<button
						className={`relative bg-blue-dark text-white py-[1px] rounded-[4px] px-[8px] ${inputFormHeightOption}`}
						onClick={onToggleMaximize}
						title={isMaximized ? '최소화' : '최대화'}
					>
						{isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
					</button>
				</div>
			</SmallTitleSpan>
					<Grid_Excel<KSK2021_R01DetailDto>
						ref={gridRef}
						rowData={paddedGridData}	getRowId={getRowId}
						columnDefs={columnDefs}		
						defaultColDef={gridDefaultColDef}

						rowClassRules={createRowClassRules<KSK2021_R01DetailDto>({
							idKey: 'valueIndex',
							selectedList: selectedKSK2021_R01DetailList,
							deletedList: deletedKSK2021_R01DetailList
						})}

						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						

						onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
						// Grid_Select에 공통 로직 처리를 위한 props 전달
						columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
						setColumnState={setDetailColumnState}		//컬럼 상태 저장
					/>
				</GridContextWrapper>
			</div>
		</>
	)
}